import type { Ref } from 'vue';
import { computed, onUnmounted, ref } from 'vue';

export interface UseScrollbarThumbOptions {
  railTopInset?: number;
  railBottomInset?: number;
  minThumbHeight?: number;
  thumbHeightFactor?: number;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<UseScrollbarThumbOptions, 'onDragStart' | 'onDragEnd'>> = {
  railTopInset: 3,
  railBottomInset: 8,
  minThumbHeight: 6,
  thumbHeightFactor: 0.65,
};

export function useScrollbarThumb(
  containerRef: Ref<HTMLElement | null>,
  options: UseScrollbarThumbOptions = {},
) {
  const {
    railTopInset,
    railBottomInset,
    minThumbHeight,
    thumbHeightFactor,
    onDragStart,
    onDragEnd,
  } = { ...DEFAULT_OPTIONS, ...options };

  const thumbHeight = ref(0);
  const thumbOffset = ref(0);
  const isDragging = ref(false);
  const dragStartY = ref(0);
  const dragStartScrollTop = ref(0);

  const isScrollable = computed(() => thumbHeight.value > 0);

  function update() {
    const el = containerRef.value;
    if (!el) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
      thumbHeight.value = 0;
      thumbOffset.value = 0;
      return;
    }

    const railHeight = Math.max(
      clientHeight - railTopInset - railBottomInset,
      0,
    );
    const proportionalThumbHeight = (clientHeight / scrollHeight) * railHeight;
    const height = Math.max(
      proportionalThumbHeight * thumbHeightFactor,
      minThumbHeight,
    );
    const maxThumbOffset = Math.max(railHeight - height, 0);
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollProgress = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;

    thumbHeight.value = height;
    thumbOffset.value = railTopInset + maxThumbOffset * scrollProgress;
  }

  function handleDragMove(event: MouseEvent) {
    if (!isDragging.value || !containerRef.value) {
      return;
    }

    const el = containerRef.value;
    const { scrollHeight, clientHeight } = el;
    const maxScrollTop = Math.max(scrollHeight - clientHeight, 0);
    if (maxScrollTop === 0) {
      return;
    }

    const railHeight = Math.max(
      clientHeight - railTopInset - railBottomInset,
      0,
    );
    const maxThumbOffset = Math.max(railHeight - thumbHeight.value, 0);
    if (maxThumbOffset === 0) {
      return;
    }

    const deltaY = event.clientY - dragStartY.value;
    const scrollRatio = maxScrollTop / maxThumbOffset;
    el.scrollTop = Math.min(
      Math.max(dragStartScrollTop.value + deltaY * scrollRatio, 0),
      maxScrollTop,
    );
    update();
  }

  function handleDragEnd() {
    if (!isDragging.value) {
      return;
    }
    isDragging.value = false;
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    onDragEnd?.();
  }

  function onThumbMouseDown(event: MouseEvent) {
    if (!containerRef.value || !isScrollable.value) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onDragStart?.();
    isDragging.value = true;
    dragStartY.value = event.clientY;
    dragStartScrollTop.value = containerRef.value.scrollTop;
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  }

  onUnmounted(() => {
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
  });

  return {
    thumbHeight,
    thumbOffset,
    isScrollable,
    isDragging,
    update,
    onThumbMouseDown,
  };
}
